import { useTranslation } from 'next-i18next'
import { format } from 'date-fns'

import ENV_VARS from '@/config'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'
import Head from '@/components/head'

const { BASE_URL } = ENV_VARS

const searchResultsPageSchema = JSON.stringify({
  '@context': 'http://schema.org',
  '@type': 'SearchResultsPage',
  lastReviewed: format(new Date(), 'yyyy-MM-dd')
})

const getCitySchema = ({ city, state, slug, description }) => `{
  "@context": "http://schema.org",
  "@type": "City",
  "description": "${description}",
  "name": "${city}",
  "url": "https://liv.rent${slug}",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "${city}",
    "addressRegion": "${state}",
    "addressCountry":{
      "@type": "Country",
      "name": "CA"
    }
  }
}`

const HeadContent = ({
  metadata,
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData = {},
  isNeighborhoodPage,
  isUniversityPage,
  isPoiPage,
  neighborhoodData = {},
  isChinese
}) => {
  const { t } = useTranslation()
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const customCities = {
    calgary: {
      image: 'apartments-for-rent-calgary.jpg',
      metaDescription: t(
        'seo.city.calgary:Calgary Meta Description',
        'Search apartments, houses and rooms for rent in Calgary, AB. Browse verified Calgary rentals, including furnished and pet-friendly apartments in Calgary.'
      )
    },
    edmonton: {
      image: 'apartments-for-rent-edmonton.jpg',
      metaDescription: t(
        'seo.city.edmonton:Edmonton Meta Description',
        'Verified apartments, condos & houses for rent in Edmonton, AB from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    vancouver: {
      image: 'apartments-for-rent-vancouver.jpg',
      metaDescription: t(
        'seo.city.vancouver:b.listings.view.page_description_vancouver.text',
        '1500+ verified apartments & houses for rent in Vancouver, BC from trusted landlords. Search condos, townhouses, rooms for rent. Furnished. Pet friendly.'
      )
    },
    burnaby: {
      image: 'apartments-for-rent-burnaby.jpg',
      metaTitle: t(
        'seo.city.burnaby:Meta Title',
        'Apartments for rent in Burnaby, BC | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.burnaby:b.listings.view.page_description_burnaby.text',
        '1500 verified apartments for rent in Burnaby, BC from trusted landlords. Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    surrey: {
      image: 'apartments-for-rent-surrey.jpg',
      metaTitle: t(
        'seo.city.surrey:Meta Title',
        'Apartments for rent in Surrey, BC | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.surrey:b.listings.view.page_description_surrey.text',
        '1500 verified apartments for rent in Surrey, BC from trusted landlords Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    richmond: {
      image: 'apartments-for-rent-richmond.jpg',
      metaDescription: t(
        'seo.city.richmond:b.listings.view.page_description_richmond.text',
        '1500+ verified apartments & houses for rent in Richmond, BC from trusted landlords. Search condos, townhouses, rooms for rent. Furnished. Pet friendly.'
      )
    },
    toronto: {
      image: 'apartments-for-rent-toronto.jpg',
      metaDescription: t(
        'seo.city.toronto:Toronto Meta Description',
        '1500+ verified apartments, condos & houses for rent in Toronto, ON from trusted landlords. Search 1 bedroom apartment, short-term rentals, rooms for rent & more'
      )
    },
    mississauga: {
      image: 'apartments-for-rent-mississauga.jpg',
      metaTitle: t(
        'seo.city.mississauga:Meta Title',
        'Apartments for rent in Mississauga, ON | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.mississauga:Mississauga Meta Description',
        '1500 verified apartments for rent in Mississauga, ON from trusted landlords. Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    markham: {
      image: 'apartments-for-rent-markham.jpg',
      metaTitle: t(
        'seo.city.markham:Meta Title',
        'Apartments for rent in Markham, ON | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.markham:Markham Meta Description',
        '1500 verified apartments for rent in Markham, ON from trusted landlords. Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    brampton: {
      image: 'apartments-for-rent-brampton.jpg',
      metaTitle: t(
        'seo.city.brampton:Meta Title',
        'Apartments for rent in Brampton, ON | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.brampton:Brampton Meta Description',
        '1500 verified apartments for rent in Brampton, ON from trusted landlords. Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    hamilton: {
      image: 'apartments-for-rent-hamilton.jpg',
      metaDescription: t(
        'seo.city.hamilton:Hamilton Meta Description',
        'Verified apartments, condos & houses for rent in Hamilton, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    kitchener: {
      image: 'apartments-for-rent-kitchener.jpg',
      metaTitle: t(
        'seo.city.kitchener:Meta Title',
        'Apartments for rent in Kitchener, ON | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.kitchener:Kitchener Meta Description',
        '1500 verified apartments for rent in Kitchener, ON from trusted landlords. Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    etobicoke: {
      image: 'apartments-for-rent-etobicoke.jpg',
      metaTitle: t(
        'seo.city.etobicoke:Meta Title',
        'Apartments for rent in Etobicoke, ON | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.etobicoke:Etobicoke Meta Description',
        '1500 verified apartments for rent in Etobicoke, ON from trusted landlords. Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    'west-vancouver': {
      image: 'apartments-for-rent-west-vancouver.jpg',
      metaDescription: t(
        'seo.city.west-vancouver:b.listings.view.page_description_west_vancouver.text',
        '1500+ verified houses & apartments for rent in West Vancouver, BC from trusted landlords. Search condos, townhouses, rooms for rent. Furnished. Pet friendly.'
      )
    },
    'north-vancouver': {
      image: 'apartments-for-rent-north-vancouver.jpg',
      metaTitle: t(
        'seo.city.north-vancouver:Meta Title',
        'Apartments for rent in North Vancouver, BC | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.north-vancouver:b.listings.view.page_description_north_vancouver.text',
        '1500 verified apartments for rent in North Vancouver, BC from trusted landlords Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    'new-westminster': {
      image: 'apartments-for-rent-new-westminster.jpg',
      metaDescription: t(
        'seo.city.new-westminster:b.listings.view.page_description_new_westminster.text',
        '1500+ verified apartments & houses for rent in New Westminster, BC from trusted landlords. Search condos, houses and rooms for rent. Furnished. Pet friendly.'
      )
    },
    langley: {
      image: 'apartments-for-rent-langley.jpg',
      metaTitle: t(
        'seo.city.langley:Meta Title',
        'Apartments for rent in Langley, BC | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.langley:b.listings.view.page_description_langley.text',
        '1500 verified apartments for rent in Langley, BC from trusted landlords Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    coquitlam: {
      image: 'apartments-for-rent-coquitlam.jpg',
      metaDescription: t(
        'seo.city.coquitlam:b.listings.view.page_description_coquitlam.text',
        '1500+ verified apartments & houses for rent in Coquitlam, BC from trusted landlords. Search condos, townhouses, rooms for rent. Furnished. Pet friendly.'
      )
    },
    scarborough: {
      image: 'apartments-for-rent-scarborough.jpg',
      metaTitle: t(
        'seo.city.scarborough:Meta Title',
        'Apartments for rent in Scarborough, ON | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.scarborough:Scarborough Meta Description',
        '1500 verified apartments for rent in Scarborough, ON from trusted landlords. Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    york: {
      image: 'apartments-for-rent-york.jpg',
      metaDescription: t(
        'seo.city.york:York Meta Description',
        'Verified apartments, condos & houses for rent in York, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    'east-york': {
      image: 'apartments-for-rent-east-york.jpg',
      metaDescription: t(
        'seo.city.east-york:East York Meta Description',
        'Verified apartments, condos & houses for rent in East York, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    'north-york': {
      image: 'apartments-for-rent-north-york.jpg',
      metaTitle: t(
        'seo.city.north-york:Meta Title',
        'Apartments for rent in North York, ON | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.north-york:North York Meta Description',
        '1500 verified apartments for rent in North York, ON from trusted landlords. Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    delta: {
      image: 'apartments-for-rent-delta.jpg',
      metaDescription: t(
        'seo.city.delta:b.listings.view.page_description_delta.text',
        'Search apartments and houses for rent in Delta, BC. Browse studio, 1 bedroom, 2 bedroom unfurnished or furnished pet-friendly apartments in Delta.'
      )
    },
    kelowna: {
      image: 'apartments-for-rent-kelowna.jpg',
      metaDescription: t(
        'seo.city.kelowna:b.listings.view.page_description_kelowna.text',
        '1500+ verified apartments & houses for rent in Kelowna, BC from trusted landlords. Search condos, townhouses, rooms for rent. Furnished. Pet friendly.'
      )
    },
    'port-coquitlam': {
      image: 'apartments-for-rent-port-coquitlam.jpg',
      metaDescription: t(
        'seo.city.port-coquitlam:b.listings.view.page_description_port_coquitlam.text',
        '1500+ verified apartments & houses for rent in Port Coquitlam, BC from trusted landlords. Search condos, townhouses, rooms for rent. Furnished. Pet friendly.'
      )
    },
    whistler: {
      image: 'apartments-for-rent-whistler.jpg',
      metaDescription: t(
        'seo.city.whistler:b.listings.view.page_description_whistler.text',
        'Search apartments and houses for rent in Whistler, BC. Browse studio, 1 bedroom, 2 bedroom unfurnished or furnished pet-friendly apartments in Whistler.'
      )
    },
    'white-rock': {
      image: 'apartments-for-rent-white-rock.jpg',
      metaDescription: t(
        'seo.city.white-rock:b.listings.view.page_description_white_rock.text',
        '1500+ verified apartments & houses for rent in White Rock, BC from trusted landlords. Search condos, houses and rooms for rent. Furnished. Pet friendly.'
      )
    },
    'port-moody': {
      image: 'apartments-for-rent-port-moody.jpg',
      metaDescription: t(
        'seo.city.port-moody:b.listings.view.page_description_port_moody.text',
        '1500+ verified apartments & houses for rent in Port Moody, BC from trusted landlords. Search condos, townhouses, rooms for rent. Furnished. Pet friendly.'
      )
    },
    abbotsford: {
      image: 'apartments-for-rent-abbotsford.jpg',
      metaDescription: t(
        'seo.city.abbotsford:b.listings.view.page_description_abbotsford.text',
        'Search apartments and houses for rent in Abbotsford, BC. Browse studio, 1 bedroom, 2 bedroom unfurnished or furnished pet-friendly apartments in Abbotsford.'
      )
    },
    vaughan: {
      image: 'apartments-for-rent-vaughan.jpg',
      metaDescription: t(
        'seo.city.vaughan:Vaughan Meta Description',
        'Verified apartments, condos & houses for rent in Vaughan, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    'richmond-hill': {
      image: 'apartments-for-rent-richmond-hill.jpg',
      metaTitle: t(
        'seo.city.richmond-hill:Meta Title',
        'Apartments for rent in Richmond Hill | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.richmond-hill:Richmond Hill Meta Description',
        '1500 verified apartments for rent in Richmond Hill, ON from trusted landlords. Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    ottawa: {
      image: 'apartments-for-rent-ottawa.jpg',
      metaDescription: t(
        'seo.city.ottawa:Ottawa Meta Description',
        'Verified apartments, condos & houses for rent in Ottawa, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    oshawa: {
      image: 'apartments-for-rent-oshawa.jpg',
      metaDescription: t(
        'seo.city.oshawa:Oshawa Meta Description',
        'Verified apartments, condos & houses for rent in Oshawa, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    ajax: {
      image: 'apartments-for-rent-ajax.jpg',
      metaDescription: t(
        'seo.city.ajax:Ajax Meta Description',
        'Verified apartments, condos & houses for rent in Ajax, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    london: {
      image: 'apartments-for-rent-london.jpg',
      metaDescription: t(
        'seo.city.london:London Meta Description',
        'Verified apartments, condos & houses for rent in London, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    barrie: {
      image: 'apartments-for-rent-barrie.jpg',
      metaDescription: t(
        'seo.city.barrie:Barrie Meta Description',
        'Verified apartments, condos & houses for rent in Barrie, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    kingston: {
      image: 'apartments-for-rent-kingston.jpg',
      metaDescription: t(
        'seo.city.kingston:Kingston Meta Description',
        'Verified apartments, condos & houses for rent in Kingston, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    guelph: {
      image: 'apartments-for-rent-guelph.jpg',
      metaDescription: t(
        'seo.city.guelph:Guelph Meta Description',
        'Verified apartments, condos & houses for rent in Guelph, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    burlington: {
      image: 'apartments-for-rent-burlington.jpg',
      metaDescription: t(
        'seo.city.burlington:Burlington Meta Description',
        'Verified apartments, condos & houses for rent in Burlington, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    'st-catharines': {
      image: 'apartments-for-rent-st-catharines.jpg',
      metaDescription: t(
        'seo.city.st-catharines:St. Catharines Meta Description',
        'Verified apartments, condos & houses for rent in St. Catharines, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    whitby: {
      image: 'apartments-for-rent-whitby.jpg',
      metaDescription: t(
        'seo.city.whitby:Whitby Meta Description',
        'Verified apartments, condos & houses for rent in Whitby, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    cambridge: {
      image: 'apartments-for-rent-cambridge.jpg',
      metaDescription: t(
        'seo.city.cambridge:Cambridge Meta Description',
        'Verified apartments, condos & houses for rent in Cambridge, ON from trusted landlords. Search 1-bedroom apartments, short-term rentals, rooms for rent & more.'
      )
    },
    montreal: {
      metaTitle: t(
        'seo.city.montreal:Meta Title',
        'Apartments for rent in Montreal, QC | Find 976 Houses and Condos'
      ),
      metaDescription: t(
        'seo.city.montreal:Montreal Meta Description',
        '1500 verified apartments for rent in Montreal, QC from trusted landlords Search condos, houses, townhouses, rooms for rent furnished, cheap, luxury'
      )
    },
    halfiax: {
      image: 'apartments-for-rent-halifax.jpg',
      metaDescription: t(
        'seo.city.halifax:Halifax Meta Description',
        'Search for apartments for rent Halifax, NS from a list of verified rentals posted by trusted landlords. Also, look for houses and rooms for rent Halifax.'
      )
    }
  }
  const customContent = customCities[cityData?.slug] || {}

  const getMetaTitle = () => {
    if (isNeighborhoodPage) {
      const locationName = `${neighborhoodData.translated} ${cityData.translated}`

      if (hasFilter) {
        const filterTitle = t(`seo:filter_title_${filterSlug}`, {
          defaultValue: '{{ cityName }}',
          cityName: locationName
        })
        return filterTitle
      }

      return t('seo:generic_search_page_title', {
        defaultValue: '{{ cityName }} Apartments, Houses and Rooms for Rent',
        cityName: locationName
      })
    }
    if (isPoiPage || isUniversityPage) {
      if (hasFilter) {
        const filterTitle = t(`seo:filter_title_${filterSlug}`, {
          defaultValue: '{{ cityName }}',
          cityName: cityNameWithState
        })
        const nearLocation = t(`seo.pois:title_near_location_name`, {
          defaultValue: 'near {{ locationName }}',
          locationName: neighborhoodData.translated
        })
        return `${filterTitle} ${nearLocation}`
      }
      return t('seo.pois:generic_search_page_poi_title', {
        defaultValue:
          'Apartments, Houses & Rooms for Rent in {{ cityName }} near {{ poiName }}',
        cityName: cityNameWithState,
        poiName: neighborhoodData.translated
      })
    }
    if (filterSlug) {
      if (filterSlug === 'apartments') {
        return t(`seo:filter_meta_title_apartments`, {
          defaultValue: '{{ cityName }}',
          cityName: cityName
        })
      }
      return t(`seo:filter_title_${filterSlug}`, {
        defaultValue: '{{ cityName }}',
        cityName: cityNameWithState
      })
    }
    if (customContent.metaTitle) {
      return customContent.metaTitle
    }
    return t('seo:generic_search_page_title', {
      defaultValue: '{{ cityName }} Apartments, Houses and Rooms for Rent',
      cityName: cityName
    })
  }

  const getMetaDescription = () => {
    if (isNeighborhoodPage) {
      if (hasFilter) {
        return t('seo:filter_neighborhood_page_description', {
          defaultValue:
            'Find {{ filterName }} in {{ neighborhoodName }}, {{ cityNameWithState }} from trusted landlords. Search condos, townhouses, rooms for rent.',
          neighborhoodName: neighborhoodData.translated,
          cityNameWithState,
          filterName
        })
      }
      return t('seo:generic_neighborhood_page_description', {
        defaultValue:
          '1500+ verified apartments & houses for rent in {{ neighborhoodName }}, {{ cityNameWithState }} from trusted landlords. Search condos, townhouses, rooms for rent.',
        neighborhoodName: neighborhoodData.translated,
        cityNameWithState
      })
    }
    if (isPoiPage || isUniversityPage) {
      const filterNameWithFallback = hasFilter
        ? filterName
        : t('seo.pois:apartments, houses & rooms for rent')

      return t('seo.pois:generic_poi_page_description', {
        defaultValue:
          'Find {{ filterName }} in {{ cityNameWithState }} near {{ poiName }}. Enjoy easy access to nearby {{ poiName }}. Browse listings today!',
        filterName: filterNameWithFallback,
        poiName: neighborhoodData.translated,
        cityNameWithState
      })
    }
    if (filterSlug) {
      return t(`seo:filter_description_${filterSlug}`, {
        defaultValue: `Search apartments for rent in {{ cityName }}. Find your home 3X faster with liv.rent - we review IDs, land titles, and other documents to confirm users' identity.`,
        cityName: cityNameWithState
      })
    }
    if (customContent.metaDescription) {
      return customContent.metaDescription
    }
    return t('seo:generic_city_page_description', {
      defaultValue:
        'Search apartments and houses for rent in {{ cityNameWithState }}. Browse studio, 1 bedroom, 2 bedroom unfurnished or furnished pet-friendly apartments in {{ cityName }}.',
      cityName,
      cityNameWithState
    })
  }

  const getPath = () => {
    const filterPath = filterSlug ? `/${filterSlug}` : ''
    if (isNeighborhoodPage || isUniversityPage || isPoiPage) {
      return `/rental-listings/city/${cityData.slug}/${neighborhoodData.slug.slug_name}${filterPath}`
    }
    return isCanadaPage
      ? `/rental-listings${filterPath}`
      : `/rental-listings/city/${cityData.slug}${filterPath}`
  }

  const metaTitle = getMetaTitle()
  const metaDescription = getMetaDescription()
  const path = getPath()
  const altTextName = neighborhoodData.translated || cityNameWithState
  const schemaData = {
    city: cityData.translated,
    state: cityData.translatedState,
    slug: path,
    description: metaDescription
  }

  return (
    <>
      <Head
        title={metaTitle}
        description={metaDescription}
        path={path}
        pageData={metadata}
        meta={{
          image: customContent.image
            ? `${BASE_URL}/images/cities/${customContent.image}`
            : undefined,
          imageAlt: isChinese
            ? `加拿大${altTextName}租房网首选`
            : `apartments for rent ${altTextName}`,
          schema: isCanadaPage ? undefined : getCitySchema(schemaData)
        }}
      />
      <script
        defer
        async
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: searchResultsPageSchema
        }}
      />
    </>
  )
}

export default HeadContent
